import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { DataTable } from 'primereact/datatable';
import { InputText } from 'primereact/inputtext';
import { Tag } from 'primereact/tag';
import { useEffect, useState } from 'react';
import { makeRequest2 } from '../../../../../utils/makeRequest';
import { toastify } from '../../../../../utils/toast';
import {
    getAgentsURL,
    resetAgentPasswordURL,
    updateAgentStatusURL
} from '../../../../../utils/urls';

// Import the specialized components
import AddAgent from '../AddAgent';
import UpdateAgent from '../UpdateAgent';

const AgentsTab = () => {
    const [agents, setAgents] = useState([]);
    const [filteredAgents, setFilteredAgents] = useState([]);
    const [globalFilter, setGlobalFilter] = useState('');
    const [loading, setLoading] = useState(false);
    const [addDialogVisible, setAddDialogVisible] = useState(false);
    const [updateDialogVisible, setUpdateDialogVisible] = useState(false);
    const [selectedAgent, setSelectedAgent] = useState(null);

    useEffect(() => {
        loadAgents();
    }, []);

    const loadAgents = async () => {
        setLoading(true);
        try {
            const response = await makeRequest2(getAgentsURL, 'GET', {});

            // Handle double-wrapped response from makeRequest2
            if (response.success || response.data || Array.isArray(response)) {
                let agentsData = [];

                // Check for double-wrapped response (makeRequest2 wraps the backend response)
                if (response.data && response.data.data && Array.isArray(response.data.data)) {
                    agentsData = response.data.data;
                } else if (response.data && Array.isArray(response.data)) {
                    agentsData = response.data;
                } else if (Array.isArray(response)) {
                    agentsData = response;
                } else if (response.success === false || (response.data && response.data.success === false)) {
                    const errorData = response.data || response;
                    toastify(errorData.message || errorData.error || 'Failed to load agents', 'error');
                    setAgents([]);
                    setFilteredAgents([]);
                    return;
                }

                setAgents(agentsData);
                setFilteredAgents(agentsData);
            } else {
                toastify(response.message || response.error || 'Failed to load agents', 'error');
                setAgents([]);
                setFilteredAgents([]);
            }
        } catch (error) {
            toastify(error.message || 'Error loading agents', 'error');
            setAgents([]);
            setFilteredAgents([]);
        }
        setLoading(false);
    };

    const onGlobalFilterChange = (e) => {
        const value = e.target.value.toLowerCase();
        setGlobalFilter(value);

        if (!value.trim()) {
            setFilteredAgents(agents);
        } else {
            const filtered = agents.filter(agent => {
                const fullName = `${agent.firstName || ''} ${agent.lastName || ''}`.trim();
                return (
                    agent.agent_id?.toLowerCase().includes(value) ||
                    fullName.toLowerCase().includes(value) ||
                    agent.name?.toLowerCase().includes(value) ||
                    agent.email?.toLowerCase().includes(value) ||
                    agent.phone?.toLowerCase().includes(value) ||
                    agent.department?.toLowerCase().includes(value) ||
                    agent.role?.toLowerCase().includes(value) ||
                    agent.status?.toLowerCase().includes(value)
                );
            });
            setFilteredAgents(filtered);
        }
    };

    const handleAddAgent = () => {
        setAddDialogVisible(true);
    };

    const handleEditAgent = (agent) => {
        setSelectedAgent(agent);
        setUpdateDialogVisible(true);
    };

    // Agent Action Functions
    const toggleAgentStatus = async (agent, newStatus, reason = '') => {
        try {
            const agentId = agent._id || agent.id;
            const url = updateAgentStatusURL.replace(':id', agentId);

            const payload = { status: newStatus };
            if (reason) {
                payload.reason = reason;
            }

            const response = await makeRequest2(url, 'POST', payload);

            if (response.success !== false && response.data && response.data.success) {
                const message = response.data.message || `Agent status updated to ${newStatus}`;
                toastify(message, 'success');

                // Reload agents to reflect changes
                loadAgents();
            } else {
                const errorMsg = response.data?.error || response.message || response.error || 'Failed to update agent status';
                toastify(errorMsg, 'error');
            }
        } catch (error) {
            toastify(error.message || 'Failed to update agent status', 'error');
        }
    };

    const confirmSuspendAgent = (agent) => {
        confirmDialog({
            message: `Are you sure you want to suspend ${agent.name || `${agent.firstName || ''} ${agent.lastName || ''}`.trim()}?`,
            header: 'Confirm Suspend',
            icon: 'pi pi-exclamation-triangle',
            accept: () => toggleAgentStatus(agent, 'suspended'),
            reject: () => { }
        });
    };

    const confirmActivateAgent = (agent) => {
        confirmDialog({
            message: `Are you sure you want to activate ${agent.name || `${agent.firstName || ''} ${agent.lastName || ''}`.trim()}?`,
            header: 'Confirm Activate',
            icon: 'pi pi-question-circle',
            accept: () => toggleAgentStatus(agent, 'active'),
            reject: () => { }
        });
    };

    const confirmTerminateAgent = (agent) => {
        confirmDialog({
            message: `Are you sure you want to terminate ${agent.name || `${agent.firstName || ''} ${agent.lastName || ''}`.trim()}? This action cannot be undone.`,
            header: 'Confirm Termination',
            icon: 'pi pi-exclamation-triangle',
            accept: () => toggleAgentStatus(agent, 'terminated'),
            reject: () => { }
        });
    };

    const resetAgentPassword = async (agent) => {
        try {
            const url = resetAgentPasswordURL.replace(':id', agent._id || agent.id);
            const response = await makeRequest2(url, 'POST');

            if (response.success !== false && response.data && response.data.success) {
                // Show detailed success message
                const message = response.data.message || 'Password reset successfully';
                toastify(message, 'success');

                // Optionally show notification status
                if (response.data.notifications) {
                    const { sms, email } = response.data.notifications;
                    if (!sms.sent && !email.sent) {
                        toastify('Warning: Failed to send credentials to agent. Please provide them manually.', 'warning');
                    }
                }

                // Reload agents to reflect any updates
                loadAgents();
            } else {
                const errorMessage = response.data?.error || response.message || 'Failed to reset password';
                toastify(errorMessage, 'error');
            }
        } catch (error) {
            toastify(error.message || 'Failed to reset password', 'error');
        }
    };

    const confirmResetPassword = (agent) => {
        confirmDialog({
            message: `Are you sure you want to reset password for ${agent.name || `${agent.firstName || ''} ${agent.lastName || ''}`.trim()}?`,
            header: 'Confirm Password Reset',
            icon: 'pi pi-exclamation-triangle',
            accept: () => resetAgentPassword(agent),
            reject: () => { }
        });
    };

    const statusBodyTemplate = (rowData) => {
        const severity = getStatusSeverity(rowData.status);
        return <Tag value={rowData.status} severity={severity} />;
    };

    const getStatusSeverity = (status) => {
        switch (status) {
            case 'active':
                return 'success';
            case 'inactive':
                return 'warning';
            case 'suspended':
                return 'danger';
            case 'training':
                return 'info';
            case 'terminated':
                return 'danger';
            default:
                return null;
        }
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                <Button
                    icon="pi pi-pencil"
                    className="p-button-rounded p-button-success p-button-sm"
                    onClick={() => handleEditAgent(rowData)}
                    tooltip="Edit Agent"
                />

                <Button
                    icon="pi pi-key"
                    className="p-button-rounded p-button-warning p-button-sm"
                    onClick={() => confirmResetPassword(rowData)}
                    tooltip="Reset Password"
                />

                {rowData.status === 'active' && (
                    <Button
                        icon="pi pi-pause"
                        className="p-button-rounded p-button-secondary p-button-sm"
                        onClick={() => confirmSuspendAgent(rowData)}
                        tooltip="Suspend Agent"
                    />
                )}

                {(rowData.status === 'suspended' || rowData.status === 'inactive') && (
                    <Button
                        icon="pi pi-play"
                        className="p-button-rounded p-button-success p-button-sm"
                        onClick={() => confirmActivateAgent(rowData)}
                        tooltip="Activate Agent"
                    />
                )}

                {rowData.status !== 'terminated' && (
                    <Button
                        icon="pi pi-times-circle"
                        className="p-button-rounded p-button-danger p-button-sm"
                        onClick={() => confirmTerminateAgent(rowData)}
                        tooltip="Terminate Agent"
                    />
                )}
            </div>
        );
    };

    const nameBodyTemplate = (rowData) => {
        return `${rowData.firstName || ''} ${rowData.lastName || ''}`.trim() || rowData.name || 'N/A';
    };

    const departmentBodyTemplate = (rowData) => {
        return rowData.department || 'N/A';
    };

    const roleBodyTemplate = (rowData) => {
        return rowData.role || 'N/A';
    };


    return (
        <>
            <div className="row">
                <div className="col-sm-12">
                    <div className="card">
                        <div className="card-header">
                            <div className="row align-items-center">
                                <div className="col-md-4">
                                    <h5 className="mb-0">Agents</h5>
                                </div>
                                <div className="col-md-8">
                                    <div className="d-flex gap-2 justify-content-end">
                                        <div className="p-input-icon-left" style={{ minWidth: '300px' }}>
                                            <i className="pi pi-search" />
                                            <InputText
                                                value={globalFilter}
                                                onChange={onGlobalFilterChange}
                                                placeholder="Search agents..."
                                                className="w-100"
                                            />
                                        </div>
                                        <button
                                            className="btn btn-primary"
                                            style={{ color: 'white', whiteSpace: 'nowrap' }}
                                            onClick={handleAddAgent}
                                        >
                                            <i className="ti ti-plus" />
                                            &nbsp;Add New Agent
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="card-body">
                            <div className="dt-responsive table-responsive">
                                <DataTable
                                    value={filteredAgents}
                                    emptyMessage="No agents found."
                                    sortMode="multiple"
                                    paginator
                                    rows={10}
                                    stripedRows
                                    tableStyle={{ minWidth: '50rem' }}
                                    loading={loading}
                                >
                                    <Column
                                        field="agent_id"
                                        header="Agent ID"
                                        sortable
                                        style={{ width: '10%' }}
                                    />
                                    <Column
                                        header="Name"
                                        body={nameBodyTemplate}
                                        sortable
                                        style={{ width: '15%' }}
                                    />
                                    <Column
                                        field="email"
                                        header="Email"
                                        sortable
                                        style={{ width: '20%' }}
                                    />
                                    <Column
                                        field="phone"
                                        header="Phone"
                                        style={{ width: '12%' }}
                                    />
                                    <Column
                                        header="Department"
                                        body={departmentBodyTemplate}
                                        sortable
                                        style={{ width: '12%' }}
                                    />
                                    <Column
                                        header="Role"
                                        body={roleBodyTemplate}
                                        sortable
                                        style={{ width: '10%' }}
                                    />
                                    <Column
                                        header="Status"
                                        body={statusBodyTemplate}
                                        sortable
                                        style={{ width: '8%' }}
                                    />
                                    <Column
                                        header="Actions"
                                        body={actionBodyTemplate}
                                        exportable={false}
                                        style={{ width: '13%' }}
                                    />
                                </DataTable>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <AddAgent
                visible={addDialogVisible}
                onHide={() => setAddDialogVisible(false)}
                onAgentAdded={loadAgents}
            />

            <UpdateAgent
                visible={updateDialogVisible}
                onHide={() => setUpdateDialogVisible(false)}
                agent={selectedAgent}
                onAgentUpdated={loadAgents}
            />

            <ConfirmDialog />
        </>
    );
};

export default AgentsTab;
